import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Facebook, Instagram, Mail, MessageCircle, Phone, MapPin } from 'lucide-react';
import { CITIES } from '../cities';
import { PHONE_E164, PHONE_DISPLAY, WHATSAPP_NUMBER, CONTACT_EMAIL, INSTAGRAM_URL, FACEBOOK_URL, TIKTOK_URL } from '../config';
import { foto } from '../imagenes';

const SITE_LINKS = [
  { to: '/catalogo', label: 'Catálogo completo' },
  { to: '/eventos', label: 'Tipos de evento' },
  { to: '/venta', label: 'Venta de equipos' },
  { to: '/precios', label: 'Precios' },
  { to: '/ciudades', label: 'Cobertura' },
  { to: '/contacto', label: 'Contacto' },
];

const Footer: React.FC = () => {
  /* En /contacto la página ya cierra con su propio llamado al cotizador: este
     botón llevaría a la misma página y competiría con él. */
  const enContacto = useLocation().pathname === '/contacto';
  return (
  <footer className="border-t border-cabina-red/20 bg-cabina-cream pb-10 pt-20">
    <div className="container mx-auto px-6">
      {/* Llamado principal */}
      {!enContacto && (
      <>
      <div className="mb-16 text-center">
        <p className="eyebrow-cdf mb-4">Contáctanos</p>
        <h2 className="mb-4 font-serif text-4xl font-bold text-cabina-red md:text-6xl">
          Tu evento en las
          <br />
          <span className="italic">mejores manos</span>
        </h2>
        <p className="mb-8 italic text-gray-600">
          ¡Habla con uno de nuestros asesores y conoce todas nuestras experiencias!
        </p>
        <Link
          to="/contacto"
          className="inline-block rounded-full border-2 border-cabina-red px-10 py-3 text-sm font-medium uppercase tracking-widest text-cabina-red transition-all hover:bg-cabina-red hover:text-white"
        >
          Contacto
        </Link>
      </div>

      <hr className="mb-12 border-cabina-red/20" />
      </>
      )}

      <div className="mb-12 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <img
            {...foto('/images/logo.png', '240px')}
            alt="Cabina de Fotos"
            width={220}
            height={64}
            className="mb-4 h-16 w-auto object-contain"
          />
          <p className="mb-5 text-sm leading-relaxed text-gray-600">
            Inmortalizando los mejores recuerdos de nuestros clientes durante más de 15 años.
          </p>
          <div className="flex gap-3">
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-cabina-red/20 text-cabina-red transition-all hover:bg-cabina-red hover:text-white"
            >
              <Instagram size={17} />
            </a>
            <a
              href={FACEBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-cabina-red/20 text-cabina-red transition-all hover:bg-cabina-red hover:text-white"
            >
              <Facebook size={17} />
            </a>
            <a
              href={TIKTOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TikTok"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-cabina-red/20 text-cabina-red transition-all hover:bg-cabina-red hover:text-white"
            >
              <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor" aria-hidden="true">
                <path d="M19.589 6.686a4.793 4.793 0 0 1-3.77-4.245V2h-3.445v13.672a2.896 2.896 0 0 1-5.201 1.743l-.002-.001.002.001a2.895 2.895 0 0 1 3.183-4.51v-3.5a6.329 6.329 0 0 0-5.394 10.692 6.33 6.33 0 0 0 10.857-4.424V8.687a8.182 8.182 0 0 0 4.773 1.526V6.79a4.831 4.831 0 0 1-1.003-.104z" />
              </svg>
            </a>
          </div>
        </div>

        <nav aria-labelledby="f-ciudades">
          <h3 id="f-ciudades" className="mb-4 text-sm font-bold uppercase tracking-wide text-cabina-red">
            Colombia
          </h3>
          <ul className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm text-gray-600">
            {CITIES.map((c) => (
              <li key={c.slug}>
                <Link to={`/ciudades/${c.slug}`} className="link-underline hover:text-cabina-red">
                  {c.name}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <nav aria-labelledby="f-sitio">
          <h3 id="f-sitio" className="mb-4 text-sm font-bold uppercase tracking-wide text-cabina-red">
            El sitio
          </h3>
          <ul className="space-y-2 text-sm text-gray-600">
            {SITE_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="link-underline hover:text-cabina-red">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div>
          <h3 className="mb-4 text-sm font-bold uppercase tracking-wide text-cabina-red">
            Información de contacto
          </h3>
          <ul className="space-y-3 text-sm text-gray-600">
            <li className="flex items-start gap-3">
              <Phone size={16} className="mt-0.5 shrink-0 text-cabina-red" />
              <a href={`tel:${PHONE_E164}`} className="hover:text-cabina-red">
                {PHONE_DISPLAY}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MessageCircle size={16} className="mt-0.5 shrink-0 text-cabina-red" />
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-cabina-red"
              >
                WhatsApp: {PHONE_DISPLAY}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Mail size={16} className="mt-0.5 shrink-0 text-cabina-red" />
              <a href={`mailto:${CONTACT_EMAIL}`} className="min-w-0 wrap-anywhere hover:text-cabina-red">
                {CONTACT_EMAIL}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin size={16} className="mt-0.5 shrink-0 text-cabina-red" />
              <span>Cobertura nacional.</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="flex flex-col items-center justify-between gap-3 border-t border-cabina-red/10 pt-8 text-sm text-gray-500 md:flex-row">
        <p>© {new Date().getFullYear()} Cabina de Fotos. Todos los derechos reservados.</p>
        <p className="italic">Alquiler de cabinas de fotos para eventos en Colombia.</p>
      </div>
    </div>
  </footer>
  );
};

export default Footer;
